import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getVendor } from 'config/vendorRegistry';
import { VERSION } from 'version';
import { PERM, Can } from 'shared/access';
import { Btn } from 'shared/ui/Btn';
import { Chip } from 'shared/ui/Chip';
import { Toggle2 } from 'shared/ui/Toggle2';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'shared/ui/Select';
import { ChangelogDialog } from './ChangelogDialog';
import { clearLicenseAccepted } from 'app/LicenseGate';
import { useVendorCtx, useConfigInputs } from 'shared/state/configContexts';
import { useTier } from 'shared/state/tierContext';
import type { AppUser } from 'shared/state/types';

export type NameplateProps = { user: AppUser; signOut: () => void };

export function Nameplate({ user, signOut }: NameplateProps) {
  const [changelogOpen, setChangelogOpen] = useState(false);
  const { vendor, accessibleVendorIds, changeVendor } = useVendorCtx();
  const { roleLabel, consultationMode, setConsultationMode, forcedConsultation } = useTier();
  const { config } = useConfigInputs();
  const isExpansion = config.systemType === 'Expansion';
  const title = process.env.REACT_APP_TITLE || 'V5000 System Configurator';
  const userName = user.email || (user.attributes?.email as string | undefined) || user.username || '';

  return (
    // The bar holds more than a narrow page column can fit on one row (logo, title,
    // vendor select, consultation toggle, user, role, three buttons). When the
    // groups could only shrink, their fixed-width children drew over one another.
    // So below 1260px of column width everything wraps: the account group drops to
    // a second row and each group wraps within itself. From 1260px it is one row
    // again: the account group keeps its width and the title truncates to make room. The query is
    // against the page column (Workbench makes it the container), so it follows the
    // pinned sidebar; with no container at all it never matches and the bar wraps.
    <div data-testid="nameplate" className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-6 py-3 [@container(min-width:1260px)]:flex-nowrap">
      <div data-testid="nameplate-identity" className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 [@container(min-width:1260px)]:flex-nowrap">
        <img src={vendor.branding.combinedLogo} alt={`${vendor.displayName} logo`} className="h-8 w-auto shrink-0" />
        <div className="min-w-0">
          <div title={title} className="truncate font-display text-base font-bold tracking-wide">{title}</div>
          <button type="button" className="whitespace-nowrap font-code text-tiny text-ink-muted hover:text-ink" onClick={() => setChangelogOpen(true)}>
            v{VERSION} · what's new
          </button>
          <ChangelogDialog open={changelogOpen} onClose={() => setChangelogOpen(false)} />
        </div>
        <Can perm={PERM.VENDOR_SELECT}>
          <Select value={vendor.id} onValueChange={changeVendor}>
            <SelectTrigger className="w-56" aria-label="Hardware vendor"><SelectValue /></SelectTrigger>
            <SelectContent>
              {accessibleVendorIds.map((id) => {
                const v = getVendor(id);
                return <SelectItem key={id} value={id}>{v.displayName} Hardware</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </Can>
        <Can perm={PERM.CONSULTATION_TOGGLE}>
          <Toggle2 leftLabel="Off" rightLabel="Consultation" ariaLabel="Consultation mode"
            isRightActive={consultationMode} forceRight={forcedConsultation} onToggle={() => setConsultationMode(!consultationMode)} />
        </Can>
      </div>
      <div data-testid="nameplate-account" className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-2 [@container(min-width:1260px)]:shrink-0 [@container(min-width:1260px)]:flex-nowrap">
        <div className="min-w-0 text-right">
          <div title={userName} className="truncate text-sm">{userName}</div>
          <div className="flex items-center justify-end gap-1.5">
            {isExpansion && <Chip tone="flash" className="whitespace-nowrap">EXPANSION</Chip>}
            <Chip tone={roleLabel === 'Special Pricing' ? 'brand' : roleLabel === 'Full Configurator' ? 'flash' : 'neutral'} className="whitespace-nowrap">{roleLabel}</Chip>
          </div>
        </div>
        <Can perm={PERM.ADMIN_VIEW}>
          <Btn asChild variant="outlined" size="sm"><Link to="/admin">Admin</Link></Btn>
        </Can>
        <Btn asChild variant="text" size="sm"><Link to="/classic">Back to classic</Link></Btn>
        <Btn variant="neutral" size="sm" onClick={() => { clearLicenseAccepted(); signOut(); }}>Sign out</Btn>
      </div>
    </div>
  );
}
